// Training Center API client.
//
// Aggregates the three legacy modules:
//   - web/scripts/training-center.js (dashboard, evals, recs, bad queries, golden eval)
//   - web/scripts/admin-semantic-model.js (semantic model editor)
//   - web/scripts/admin-prompt-examples.js (prompt examples editor)
// Endpoint contracts are identical to the legacy fetch calls. Same
// Authorization Bearer + X-Source: web headers.

function authHeaders(bearer: string): HeadersInit {
  return { Authorization: `Bearer ${bearer}`, "X-Source": "web" };
}

function jsonHeaders(bearer: string): HeadersInit {
  return { ...authHeaders(bearer), "Content-Type": "application/json" };
}

function qs(params: object | undefined): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      v.forEach((x) => usp.append(k, String(x)));
      continue;
    }
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function toError(r: Response): Promise<Error> {
  let detail = "";
  try {
    const data = await r.json();
    detail = data?.detail || data?.message || JSON.stringify(data);
  } catch {
    detail = await r.text().catch(() => "");
  }
  const err = new Error(`HTTP ${r.status}${detail ? `: ${detail}` : ""}`);
  (err as Error & { status?: number }).status = r.status;
  return err;
}

async function jget<T>(bearer: string, path: string, params?: object): Promise<T> {
  const r = await fetch(`${path}${qs(params)}`, { headers: authHeaders(bearer) });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function jpost<T>(bearer: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method: "POST",
    headers: jsonHeaders(bearer),
    body: JSON.stringify(body ?? {}),
  });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function jput<T>(bearer: string, path: string, body: unknown): Promise<T> {
  const r = await fetch(path, {
    method: "PUT",
    headers: jsonHeaders(bearer),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function jdel(bearer: string, path: string): Promise<void> {
  const r = await fetch(path, { method: "DELETE", headers: authHeaders(bearer) });
  if (!r.ok) throw await toError(r);
}

// ---------------------------------------------------------------------------
// Org scope (shared with admin client)
// ---------------------------------------------------------------------------

export interface OrgScope {
  organization?: string;
}

// ---------------------------------------------------------------------------
// Dashboard — KPIs + 4 trend charts
// ---------------------------------------------------------------------------

export interface DashboardKpi {
  quality_score?: number | null;
  avg_relevance?: number | null;
  avg_sql_correctness?: number | null;
  scored_queries_30d?: number | null;
  feedback_positive_rate?: number | null;
  pending_recommendations?: number | null;
  golden_pass_rate?: number | null;
  golden_last_run_at?: string | null;
}

export interface QualityTrendPoint {
  day: string;
  overall?: number | null;
  relevance?: number | null;
  sql_correctness?: number | null;
}

export interface VolumeTrendPoint {
  day: string;
  count: number;
}

export interface LatencyTrendPoint {
  day: string;
  avg_ms?: number | null;
  p95_ms?: number | null;
}

export interface FeedbackTrendPoint {
  day: string;
  good?: number;
  bad?: number;
}

export interface DashboardData {
  kpis: DashboardKpi;
  quality_trend: QualityTrendPoint[];
  latency_trend: LatencyTrendPoint[];
  volume_trend: VolumeTrendPoint[];
  feedback_trend: FeedbackTrendPoint[];
}

// ---------------------------------------------------------------------------
// Evals — paginated query list (uses /admin/queries — same as legacy
// Training Center Evals tab)
// ---------------------------------------------------------------------------

export interface QueryEvalRow {
  id: number;
  query_id?: string;
  trace_id?: string | null;
  thread_id?: number | null;
  raw_query: string;
  created_at: string;
  user_email: string;
  organization: string;
  source: string;
  datasource_name?: string | null;
  status?: string | null;
  execution_time_ms?: number | null;
  sql_generated?: string | null;
  overall_score?: number | null;
  feedback_rating?: "Good" | "Bad" | null;
  feedback_comment?: string | null;
}

export interface EvalsResponse {
  items: QueryEvalRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface QualityScore {
  score_name: string;
  score_value: number | null;
  comment: string | null;
  evaluator_model: string | null;
  created_at: string;
}

export interface QualityDetails {
  trace_id: string;
  overall_score: number | null;
  scores: QualityScore[];
}

// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------

export type RecommendationStatus = "pending" | "applied" | "dismissed" | "in_progress";

export interface Recommendation {
  id: number;
  rec_type: string;
  organization: string;
  status: RecommendationStatus;
  title: string;
  description?: string;
  rationale?: string;
  impact_score?: number | null;
  evidence?: Record<string, unknown>;
  proposed_change?: Record<string, unknown>;
  applied_at?: string | null;
  applied_by?: string | null;
  created_at: string;
}

export interface AnalyzeRun {
  id: number;
  organization: string;
  status: "queued" | "in_progress" | "completed" | "failed";
  recommendations_created?: number;
  created_at: string;
  finished_at?: string | null;
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Promotable / Bad Queries
// ---------------------------------------------------------------------------

export interface PromotableQueryRow {
  view_id: string;
  query_id?: string;
  raw_query: string;
  generated_sql?: string;
  organization: string;
  user_email: string;
  source: string;
  datasource_name?: string;
  feedback?: "Good";
  created_at: string;
  overall_score?: number | null;
}

export interface BadQueryRow {
  view_id: string;
  query_id?: string;
  raw_query: string;
  generated_sql?: string;
  organization: string;
  user_email: string;
  source: string;
  datasource_name?: string;
  feedback?: "Bad";
  created_at: string;
  reason?: string | null;
}

// ---------------------------------------------------------------------------
// Golden Eval
// ---------------------------------------------------------------------------

export interface GoldenDataset {
  id: number;
  organization: string;
  name: string;
  description?: string;
  item_count: number;
  last_run_at?: string | null;
  last_pass_rate?: number | null;
}

export interface GoldenItem {
  id: number;
  dataset_id: number;
  query: string;
  expected_sql?: string;
  expected_tables?: string[];
  expected_chart_type?: string;
  expected_data?: Record<string, unknown>;
  user_context?: { email?: string; organization?: string };
}

export interface GoldenRun {
  id: number;
  dataset_id: number;
  status: "queued" | "in_progress" | "completed" | "failed";
  pass_rate?: number | null;
  passed?: number;
  failed?: number;
  created_at: string;
  finished_at?: string | null;
}

// ---------------------------------------------------------------------------
// Prompt Examples
// ---------------------------------------------------------------------------

// Backend uses `datasource` (not datasource_name) and exposes the params
// metadata at the summary level too. Match SqlConfigSummary exactly.
export interface PromptExampleConfig {
  id: number;
  organization: string;
  datasource: string | null;
  stage: string;
  param_keys: string[];
  example_count: number;
  updated_at: string;
  params_source: string;
}

export interface PromptExample {
  id: number;
  config_id: number;
  example_id: string;
  title: string;
  user_query: string;
  prompt_text: string;
  always_include: boolean;
  conditional_tables: string[] | null;
  display_order: number;
  source: string;
  source_query_id: string | null;
  derived_from_id: number | null;
  created_at: string;
}

// Detail response is flat — SqlConfigDetail extends SqlConfigSummary and
// adds a `params` dict. No outer wrapper.
export interface PromptExampleConfigDetail extends PromptExampleConfig {
  params: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Semantic Model (light wrappers — the editor is form-driven, not typed
// strictly; the legacy code passes JSON blobs through unmodified)
// ---------------------------------------------------------------------------

export interface SMConfig {
  id: number;
  organization: string;
  version: number;
  status: "draft" | "published" | "archived";
  published_at?: string | null;
  created_at?: string | null;
  organization_context?: string | null;
  notes?: string | null;
  created_by?: string | null;
  published_by?: string | null;
}

// Backend /configs/{id} returns a flat dict — SMConfig fields at the top
// level, plus datasources + metrics_count + cross_datasource_relationships.
export interface SMConfigDetail extends SMConfig {
  datasources: SMDatasource[];
  metrics_count: number;
  cross_datasource_relationships?: SMCrossDsRelationship[];
}

export interface SMDatasource {
  id: number;
  config_id?: number;
  name: string;
  description?: string | null;
  // Present on summary (returned inside /configs/{id}); absent on
  // /datasources/{id} (where tables[] is returned directly).
  table_count?: number;
  storage_type?: string | null;
}

// /datasources/{id} returns a flat dict with SMDatasource fields plus
// `tables` and a few advanced configuration fields.
export interface SMDatasourceDetail extends SMDatasource {
  relationships?: unknown[];
  table_inheritance?: Record<string, unknown>;
  prompt_additional_instructions?: string | null;
  upfront_queries?: unknown[];
  tables: SMTable[];
}

export interface SMTable {
  id: number;
  datasource_id?: number;
  table_name: string;
  schema_name?: string | null;
  description?: string | null;
  display_name?: string | null;
  label?: string | null;
  // Counts returned only on the datasource-detail (Level 3) tables list.
  column_count?: number;
  entity_count?: number;
  measure_count?: number;
  review_status?: string | null;
  updated_at?: string | null;
}

export interface SMColumn {
  name: string;
  type?: string;
  description?: string | null;
  primary_key?: boolean;
  choice_field?: Record<string, string> | null;
  foreign_key?: { table: string; column: string } | null;
}

export interface SMEntity {
  type: string;
  column?: string;
  expression?: string;
  description?: string | null;
  // Free-form passthrough; legacy SM stores extra fields per entity.
  [k: string]: unknown;
}

export interface SMMeasure {
  description?: string | null;
  units?: string | null;
  aggregation?: string | null;
  expression?: string | null;
  [k: string]: unknown;
}

// /tables/{id} returns flat columns/entities/measures dicts.
export interface SMTableDetail extends SMTable {
  module?: string | null;
  columns: Record<string, SMColumn>;
  entities: SMEntity[];
  measures: Record<string, SMMeasure>;
  storage_overrides?: Record<string, unknown>;
  updated_by?: string | null;
}

export interface SMMetric {
  id: number;
  name: string;
  display_name?: string | null;
  description?: string | null;
  metric_type?: string | null;
  source_table?: string | null;
  source_measure?: string | null;
  calculation?: string | null;
  units?: string | null;
  currency?: string | null;
  scale?: string | null;
  aggregation?: string | null;
  tracked_by?: string | null;
  base_metrics?: unknown[];
  components?: unknown;
  other_names?: string[];
  associated_metrics?: unknown[];
  cuts?: Record<string, unknown>;
  display_order?: number;
}

export interface SMCrossDsRelationship {
  from_datasource?: string;
  from_table?: string;
  from_column?: string;
  to_datasource?: string;
  to_table?: string;
  to_column?: string;
  description?: string | null;
  [k: string]: unknown;
}

// ---------------------------------------------------------------------------
// Endpoint surface
// ---------------------------------------------------------------------------

export const trainingApi = {
  // Dashboard — KPIs + non-quality trends come from /admin/overview
  // (same endpoint Admin Console uses). The multi-line quality trend
  // (overall/relevance/sql/entity/output) has its own endpoint.
  qualityTrend(
    bearer: string,
    scope: OrgScope,
  ): Promise<Array<{ day: string; overall?: number; relevance?: number; sql?: number; entity?: number; output?: number }>> {
    return jget<{
      items?: Array<{ day: string; overall?: number; relevance?: number; sql?: number; entity?: number; output?: number }>;
    }>(bearer, "/api/admin/quality/trend", scope).then((d) => d.items ?? []);
  },

  // Evals — mirrors the legacy /admin/queries grid (paginated query list
  // with quality_score per row). /admin/quality/details is single-trace
  // and 422s without a trace_id.
  evals(
    bearer: string,
    params: OrgScope & { search?: string; page?: number; page_size?: number },
  ): Promise<EvalsResponse> {
    return jget(bearer, "/api/admin/queries", params);
  },

  // Per-trace quality breakdown — backs the Evals row drawer.
  qualityDetails(bearer: string, params: OrgScope & { trace_id: string }): Promise<QualityDetails> {
    return jget(bearer, "/api/admin/quality/details", params);
  },

  // Recommendations — backend returns a bare list[dict], not a wrapper.
  recommendations(
    bearer: string,
    params: OrgScope & { status?: RecommendationStatus | "all" },
  ): Promise<Recommendation[]> {
    return jget(bearer, "/api/admin/training-center/recommendations/", params);
  },

  analyze(bearer: string, scope: OrgScope): Promise<{ run_id: number; status: string }> {
    return jpost(bearer, "/api/admin/training-center/recommendations/analyze", scope);
  },

  applyRecommendation(bearer: string, recId: number): Promise<{ status: string }> {
    return jpost(bearer, `/api/admin/training-center/recommendations/${recId}/apply`);
  },

  dismissRecommendation(bearer: string, recId: number, reason?: string): Promise<{ status: string }> {
    return jpost(bearer, `/api/admin/training-center/recommendations/${recId}/dismiss`, { reason });
  },

  recommendationRuns(bearer: string, scope: OrgScope): Promise<AnalyzeRun[]> {
    return jget(bearer, "/api/admin/training-center/recommendations/runs", scope);
  },

  // Promotable / Bad Queries
  promotableQueries(
    bearer: string,
    params: OrgScope & { page?: number; page_size?: number },
  ): Promise<{ items: PromotableQueryRow[]; total: number }> {
    return jget(bearer, "/api/admin/training-center/promotable-queries", params);
  },

  badQueries(
    bearer: string,
    params: OrgScope & { page?: number; page_size?: number },
  ): Promise<{ items: BadQueryRow[]; total: number }> {
    return jget(bearer, "/api/admin/training-center/bad-queries", params);
  },

  promoteQuery(
    bearer: string,
    body: { view_id: string; stages?: string[] },
  ): Promise<{ created: number }> {
    return jpost(bearer, "/api/admin/training-center/promote", body);
  },

  dismissBadQuery(bearer: string, viewId: string): Promise<{ status: string }> {
    return jpost(bearer, "/api/admin/training-center/dismiss-bad-query", { view_id: viewId });
  },

  // Golden Eval
  goldenDatasets(bearer: string, scope: OrgScope): Promise<GoldenDataset[]> {
    return jget(bearer, "/api/admin/training-center/golden-eval/datasets", scope);
  },

  goldenItems(
    bearer: string,
    params: { dataset_id: number },
  ): Promise<{ items: GoldenItem[] }> {
    return jget(bearer, "/api/admin/training-center/golden-eval/items", params);
  },

  goldenRunStart(
    bearer: string,
    body: { dataset_id: number },
  ): Promise<{ run_id: number; status: string }> {
    return jpost(bearer, "/api/admin/training-center/golden-eval/run", body);
  },

  goldenRuns(bearer: string, params: { dataset_id?: number }): Promise<GoldenRun[]> {
    return jget(bearer, "/api/admin/training-center/golden-eval/runs", params);
  },

  // Prompt Examples — backend returns bare arrays for list endpoints.
  peConfigs(bearer: string, scope: OrgScope): Promise<PromptExampleConfig[]> {
    return jget(bearer, "/api/admin/prompt-examples/configs", scope);
  },

  peConfigDetail(bearer: string, configId: number): Promise<PromptExampleConfigDetail> {
    return jget(bearer, `/api/admin/prompt-examples/configs/${configId}`);
  },

  peExamples(bearer: string, configId: number): Promise<PromptExample[]> {
    return jget(bearer, `/api/admin/prompt-examples/configs/${configId}/examples`);
  },

  peUpdateExample(
    bearer: string,
    configId: number,
    exampleId: number,
    body: { example_input?: string; example_output?: string; params_source?: string },
  ): Promise<PromptExample> {
    return jput(bearer, `/api/admin/prompt-examples/configs/${configId}/examples/${exampleId}`, body);
  },

  peDeleteExample(bearer: string, configId: number, exampleId: number): Promise<void> {
    return jdel(bearer, `/api/admin/prompt-examples/configs/${configId}/examples/${exampleId}`);
  },

  peUpdateParams(
    bearer: string,
    configId: number,
    params: Record<string, unknown>,
  ): Promise<{ params: Record<string, unknown> }> {
    return jput(bearer, `/api/admin/prompt-examples/configs/${configId}/params`, { params });
  },

  peProductionQueries(
    bearer: string,
    configId: number,
  ): Promise<{ items: Array<{ raw_query: string; query_id: string; created_at: string }> }> {
    return jget(bearer, `/api/admin/prompt-examples/configs/${configId}/production-queries`);
  },

  pePromoteQuery(
    bearer: string,
    configId: number,
    body: { query_id: string },
  ): Promise<{ created: number }> {
    return jpost(bearer, `/api/admin/prompt-examples/configs/${configId}/promote-query`, body);
  },

  // Semantic Model — bare array return.
  smConfigs(bearer: string, scope: OrgScope): Promise<SMConfig[]> {
    return jget(bearer, "/api/admin/semantic-model/configs", scope);
  },

  smConfigDetail(bearer: string, configId: number): Promise<SMConfigDetail> {
    return jget(bearer, `/api/admin/semantic-model/configs/${configId}`);
  },

  smDatasourceDetail(bearer: string, datasourceId: number): Promise<SMDatasourceDetail> {
    return jget(bearer, `/api/admin/semantic-model/datasources/${datasourceId}`);
  },

  smTableDetail(bearer: string, tableId: number): Promise<SMTableDetail> {
    return jget(bearer, `/api/admin/semantic-model/tables/${tableId}`);
  },

  smMetrics(bearer: string, configId: number): Promise<SMMetric[]> {
    return jget(bearer, `/api/admin/semantic-model/configs/${configId}/metrics`);
  },

  smCreateDraft(bearer: string, configId: number): Promise<{ id: number }> {
    return jpost(bearer, `/api/admin/semantic-model/configs/${configId}/create-draft`);
  },

  smValidate(
    bearer: string,
    configId: number,
  ): Promise<{ errors: unknown[]; warnings: unknown[]; ok: boolean }> {
    return jpost(bearer, `/api/admin/semantic-model/configs/${configId}/validate`);
  },

  smPublish(bearer: string, configId: number): Promise<{ status: string }> {
    return jpost(bearer, `/api/admin/semantic-model/configs/${configId}/publish`);
  },

  smDiscardDraft(bearer: string, configId: number): Promise<void> {
    return jdel(bearer, `/api/admin/semantic-model/configs/${configId}`);
  },
};
